import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  CircularProgress,
  Divider,
  Paper,
  styled,
  Typography,
} from '@mui/material';
import { gql, useMutation } from '@apollo/client';

// Define styled components using the styled API
const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
}));

const Title = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontSize: '1.5rem',
  [theme.breakpoints.up('md')]: {
    fontSize: '2rem',
  },
}));

const Subtitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  fontSize: '1rem',
  [theme.breakpoints.up('md')]: {
    fontSize: '1.25rem',
  },
}));

const StyledDivider = styled(Divider)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(2),
}));

const ButtonContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
  },
}));

const FullWidthButton = styled(Button)(({ theme }) => ({
  flex: 1,
  [theme.breakpoints.down('sm')]: {
    width: '100%',
  },
}));

// Mutation relies on X-Hasura-Patient-Id session variable for row-level security
// No need to pass patientId - Hasura filters automatically based on the verified token
const UPDATE_PATIENT_OPTOUT = gql`
  mutation UpdatePatientOptOut {
    update_dans_fund_patients(
      where: {}
      _set: { opted_out: true, opted_out_at: "now()" }
    ) {
      affected_rows
      returning {
        id
        opted_out
      }
    }
  }
`;

const DansFundOptOut = (): React.ReactElement => {
  const { token } = useParams<{ token?: string }>();
  const [confirmationShown, setConfirmationShown] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Mutation for updating opt-out status
  // Row-level security ensures only the patient's own record is updated
  const [updateOptOutStatus, { loading }] = useMutation(UPDATE_PATIENT_OPTOUT, {
    onCompleted(data) {
      if (data.update_dans_fund_patients?.affected_rows > 0) {
        setConfirmed(true);
      } else {
        setError('Could not process opt-out request. The link may be invalid or expired.');
      }
    },
    onError(err) {
      console.error('Opt-out mutation error:', err);
      setError(err.message || 'An error occurred while processing your request.');
    },
  });

  const handleConfirm = () => {
    setConfirmationShown(true);
  };

  const handleOptOut = () => {
    if (!token) {
      setError('Invalid link. Please use the link from your SMS.');
      return;
    }
    // No variables needed - Hasura uses X-Hasura-Patient-Id from verified token
    updateOptOutStatus();
  };


  // Show loading state while mutation is in progress
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Show success message if opt-out was successful
  if (confirmed) {
    return (
      <Box sx={{ p: 2, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <StyledPaper>
          <Title variant="h5" align="center">Opt-Out Successful</Title>
          <StyledDivider />
          <Alert severity="success" sx={{ mt: 2, mb: 2 }}>
            <AlertTitle>Success</AlertTitle>
            You have successfully opted out of further communications.
          </Alert>
          <Typography paragraph>
            You will no longer receive check-in messages or other communications
            from Dan's Fund For Burns.
          </Typography>
        </StyledPaper>
      </Box>
    );
  }

  // Show error message if there was an error
  if (error) {
    return (
      <Box sx={{ p: 2, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <StyledPaper>
          <Title variant="h5" align="center">Opt-Out Error</Title>
          <StyledDivider />
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            <AlertTitle>Error</AlertTitle>
            {error}
          </Alert>
          <Typography paragraph>
            We encountered an issue processing your opt-out request. Please try
            again later or contact Dan's Fund For Burns directly at 07526847699
            or email info@dansfundforburns.org.
          </Typography>
        </StyledPaper>
      </Box>
    );
  }

  // Show confirmation dialog if confirmation button was clicked
  if (confirmationShown) {
    return (
      <Box sx={{ p: 2, maxWidth: 600, mx: 'auto', mt: 4 }}>
        <StyledPaper>
          <Title variant="h5" align="center">Confirm Opt-Out</Title>
          <StyledDivider />
          <Typography paragraph>
            Are you sure you want to opt out of receiving further communications
            from Dan's Fund For Burns?
          </Typography>
          <Typography paragraph>
            If you opt out, you will no longer receive check-in messages or
            other communications related to your burn recovery.
          </Typography>
          <ButtonContainer>
            <FullWidthButton variant="outlined" color="primary"
              onClick={() => setConfirmationShown(false)}>
              Cancel
            </FullWidthButton>
            <FullWidthButton variant="contained" color="error"
              onClick={handleOptOut}>
              Yes, Opt Out
            </FullWidthButton>
          </ButtonContainer>
        </StyledPaper>
      </Box>
    );
  }

  // Initial opt-out page
  return (
    <Box sx={{ p: 2, maxWidth: 600, mx: 'auto', mt: 4 }}>
      <StyledPaper>
        <Title variant="h5" align="center">Dan's Fund For Burns - Opt
          Out</Title>
        <StyledDivider />
        <Subtitle variant="h6">
          You are about to opt out of communications from Dan's Fund For Burns
        </Subtitle>
        <Typography paragraph>
          Dan's Fund For Burns provides support and assistance to adult burn
          survivors across the UK.
        </Typography>
        <Typography paragraph>
          By opting out, you will no longer receive check-in messages or
          questionnaires that could help connect you with support services.
        </Typography>
        <Typography paragraph>
          You can always get in touch directly with Dan's Fund For Burns at any
          time by calling 07526847699 or emailing info@dansfundforburns.org.
        </Typography>
        <ButtonContainer>
          <FullWidthButton variant="contained" color="primary"
            onClick={handleConfirm}>
            Continue to Opt Out
          </FullWidthButton>
        </ButtonContainer>
      </StyledPaper>
    </Box>
  );
};

export default DansFundOptOut;
